import { useCallback, useState, useEffect } from 'react';
import jwt_decode from 'jwt-decode';
import axios from 'axios';
import { User } from '../../types/Types';

const AuthHooks = () => {
  const [accessToken, setAccessToken] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [tokenExpirationDate, setTokenExpirationDate] = useState('');
  const [userId, setUserId] = useState('');
  const [currentUser, setCurrentUser] = useState<User>({} as User);

  const getCurrentUser = useCallback(async () => {
    try {
      if (userId) {
        const res: any = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/users/${userId}`
        );
        setCurrentUser(res.data);
      } else {
        setCurrentUser({} as User);
      }
    } catch (err: any) {
      if (err.response) {
        console.log(err.response.data.message);
      } else {
        console.log(err.message);
      }
    }
  }, [userId]);

  const login = useCallback((data: any) => {
    try {
      const decodeToken: any = jwt_decode(data.accessToken);

      const expirationDate = data.expiration
        ? data.expiration
        : new Date(decodeToken.exp * 1000);
      // const expirationDate =
      //   data.expiration || new Date(new Date().getTime() + 1000 * 60 * 180);

      setTokenExpirationDate(expirationDate);
      setAccessToken(data.accessToken);
      setRefreshToken(data.refreshToken);
      setUserId(data.id);

      localStorage.setItem(
        'userData',
        JSON.stringify({
          id: data.id,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          expiration: expirationDate,
        })
      );
    } catch (err: any) {
      if (err.response) {
        console.log(err.response.data.message);
      } else {
        console.log(err.message);
      }
    }
  }, []);

  const logout = useCallback(async () => {
    window.location.href = '/';

    setTokenExpirationDate('');
    setAccessToken('');
    setRefreshToken('');
    setUserId('');
    localStorage.removeItem('userData');

    try {
      const res: any = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/logout`,
        accessToken,
        {
          headers: { authorization: 'Bearer ' + accessToken },
        }
      );
      console.log(res.data);
    } catch (err: any) {
      if (err.response) {
        console.log(err.response.data.message);
      } else {
        console.log(err.message);
      }
    }
  }, [accessToken]);

  const refreshTokenHandler = async () => {
    try {
      const res: any = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/refresh`,
        {
          token: refreshToken,
        }
      );
      const decodeToken: any = jwt_decode(res.data.accessToken);
      const expDate = new Date(decodeToken.exp * 1000);
      const expirationDate = expDate;
      // const expirationDate = new Date(new Date().getTime() + 1000 * 60 * 60);
      localStorage.setItem(
        'userData',
        JSON.stringify({
          id: userId,
          expiration: expirationDate,
          accessToken: res.data.accessToken,
          refreshToken: res.data.refreshToken,
        })
      );
      return res.data;
    } catch (err: any) {
      if (err.response) {
        console.log(err.response.data.message);
      } else {
        console.log(err.message);
      }
    }
  };

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(
    async (config: any) => {
      let currentDate = new Date();
      const decodedToken: any = jwt_decode(accessToken);
      if (decodedToken.exp * 1000 < currentDate.getTime()) {
        const data = await refreshTokenHandler();
        config.headers['authorization'] = 'Bearer ' + data.accessToken;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  useEffect(() => {
    const checkTokenTimeout = async () => {
      let logoutTimer: any;
      if (accessToken && tokenExpirationDate) {
        const decodedToken: any = await jwt_decode(accessToken);
        const expirationDate = new Date(decodedToken.exp * 1000);

        const remainingTime = expirationDate.getTime() - new Date().getTime();
        logoutTimer = setTimeout(logout, remainingTime);
      } else {
        clearTimeout(logoutTimer);
      }
    };
    checkTokenTimeout();
  }, [accessToken, logout, tokenExpirationDate]);

  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem('userData') || '{}');
    if (
      storedData &&
      storedData.accessToken &&
      new Date(storedData.expiration) > new Date()
    ) {
      login({
        id: storedData.id,
        accessToken: storedData.accessToken,
        refreshToken: storedData.refreshToken,
        expiration: storedData.expiration,
      });
    } else if (
      storedData &&
      storedData.accessToken &&
      new Date(storedData.expiration) < new Date()
    ) {
      logout();
    }
  }, [login, logout]);

  return {
    getCurrentUser,
    login,
    logout,
    currentUser,
    axiosJWT,
    accessToken,
    userId,
  };
};

export default AuthHooks;
